class LetoileScrollRails {
  constructor(root = document) {
    this.buttons = letoileElements(root, '[data-rail-prev], [data-rail-next]')
      .filter((button) => !button.dataset.railBound);
    this.animations = new WeakMap();
    this.resizeObservers = [];
    this.sections = new Set();
    this.bind();
  }

  carouselEasing(progress) {
    const sample = (time, first, second) => (
      (3 * ((1 - time) ** 2) * time * first)
      + (3 * (1 - time) * (time ** 2) * second)
      + (time ** 3)
    );
    const derivative = (time, first, second) => (
      (3 * ((1 - time) ** 2) * first)
      + (6 * (1 - time) * time * (second - first))
      + (3 * (time ** 2) * (1 - second))
    );

    let time = progress;
    for (let iteration = 0; iteration < 5; iteration += 1) {
      const slope = derivative(time, 0.42, 0.27);
      if (Math.abs(slope) < 0.0001) break;
      time -= (sample(time, 0.42, 0.27) - progress) / slope;
      time = Math.min(1, Math.max(0, time));
    }
    return sample(time, 0.65, 0.99);
  }

  animateHomeRail(rail, targetPosition) {
    const activeAnimation = this.animations.get(rail);
    if (activeAnimation) window.cancelAnimationFrame(activeAnimation);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      rail.scrollLeft = targetPosition;
      return;
    }

    const startPosition = rail.scrollLeft;
    const distance = targetPosition - startPosition;
    if (Math.abs(distance) < 1) {
      rail.scrollLeft = targetPosition;
      return;
    }

    const duration = 400;
    const startedAt = window.performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      rail.scrollLeft = startPosition + (distance * this.carouselEasing(progress));
      if (progress < 1) {
        this.animations.set(rail, window.requestAnimationFrame(step));
      } else {
        rail.scrollLeft = targetPosition;
        this.animations.delete(rail);
      }
    };

    this.animations.set(rail, window.requestAnimationFrame(step));
  }

  logicalPosition(rail) { return Math.abs(rail.scrollLeft); }
  signedPosition(rail, position) { return window.getComputedStyle(rail).direction === 'rtl' ? -position : position; }
  railItemPositions(rail) {
    const items = [...rail.children].filter((item) => !item.hidden && item.getBoundingClientRect().width > 0);
    const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
    if (!items.length) return [0, maxScroll];
    const origin = items[0].offsetLeft;
    return items
      .map((item) => Math.min(maxScroll, Math.max(0, Math.abs(item.offsetLeft - origin))))
      .concat([0, maxScroll])
      .sort((a, b) => a - b)
      .filter((position, index, list) => index === 0 || Math.abs(position - list[index - 1]) > 1);
  }

  nextItemPosition(rail, direction) {
    const current = this.logicalPosition(rail);
    const positions = this.railItemPositions(rail);
    if (direction > 0) {
      return positions.find((position) => position > current + 2) ?? positions.at(-1) ?? 0;
    }
    return [...positions].reverse().find((position) => position < current - 2) ?? positions[0] ?? 0;
  }

  bind() {
    this.buttons.forEach((button) => {
      button.dataset.railBound = 'true';
      const section = button.closest('[data-scroll-rail-section], section');
      const rail = section?.querySelector('[data-scroll-rail]');
      if (!section || !rail) return;
      this.sections.add(section);
      if (!rail.dataset.railPositionInitialized) {
        rail.dataset.railPositionInitialized = 'true';
        rail.scrollLeft = 0;
      }
      button.addEventListener('click', () => {
        const direction = button.hasAttribute('data-rail-prev') ? -1 : 1;
        const nextPosition = this.nextItemPosition(rail, direction);
        const signedPosition = this.signedPosition(rail, nextPosition);
        if (section.closest('[data-native-home]')) this.animateHomeRail(rail, signedPosition);
        else rail.scrollLeft = signedPosition;
        window.requestAnimationFrame(() => this.sync(section, rail));
      });
      rail.addEventListener('scroll', () => this.sync(section, rail), { passive: true });
    });
    this.sections.forEach((section) => this.sync(section, section.querySelector('[data-scroll-rail]')));
    if (this.sections.size) {
      const syncAll = () => this.sections.forEach((section) => this.sync(section, section.querySelector('[data-scroll-rail]')));
      window.addEventListener('resize', syncAll, { passive: true });
      window.addEventListener('load', syncAll, { once: true });
      document.fonts?.ready.then(syncAll).catch(() => {});

      if (typeof ResizeObserver === 'function') {
        this.sections.forEach((section) => {
          const rail = section.querySelector('[data-scroll-rail]');
          if (!rail) return;
          const observer = new ResizeObserver(() => this.sync(section, rail));
          observer.observe(rail);
          [...rail.children].forEach((child) => observer.observe(child));
          this.resizeObservers.push(observer);
        });
      }

      this.sections.forEach((section) => {
        const rail = section.querySelector('[data-scroll-rail]');
        if (!rail || rail.dataset.railMutationObserved) return;
        rail.dataset.railMutationObserved = 'true';
        new MutationObserver(() => this.sync(section, rail))
          .observe(rail, { childList: true, subtree: true });
      });
    }
  }

  sync(section, rail) {
    if (!rail) return;
    const controls = section.querySelector('.le-native-arrows--controls');
    const prev = section.querySelector('[data-rail-prev]');
    const next = section.querySelector('[data-rail-next]');
    const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const hasOverflow = maxScroll > 2;
    if (controls) controls.hidden = !hasOverflow;
    if (!prev || !next) return;
    const position = this.logicalPosition(rail);
    prev.disabled = !hasOverflow || position <= 2;
    next.disabled = !hasOverflow || position >= maxScroll - 2;
  }
}

new LetoileScrollRails();

class LetoileHomeTabs {
  constructor(root = document) {
    this.sections = letoileElements(root, '[data-home-tabs]').filter((section) => !section.dataset.homeTabsBound);
    this.sections.forEach((section) => this.bind(section));
  }

  bind(section) {
    const tabs = [...section.querySelectorAll('[data-home-tab]')];
    if (!tabs.length) return;

    section.dataset.homeTabsBound = 'true';
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activate(section, tab.dataset.homeTab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const currentIndex = tabs.indexOf(tab);
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextTab = tabs[(currentIndex + direction + tabs.length) % tabs.length];
        this.activate(section, nextTab.dataset.homeTab);
        nextTab.focus();
      });
    });

    const activeTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    this.activate(section, activeTab.dataset.homeTab);
  }

  activate(section, selectedId) {
    section.dataset.homeTabsActive = selectedId;

    section.querySelectorAll('[data-home-tab]').forEach((tab) => {
      const selected = tab.dataset.homeTab === selectedId;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    section.querySelectorAll('[data-home-tab-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.homeTabPanel !== selectedId;
    });

    const featureBanner = section.querySelector('[data-home-tab-feature-banner]');
    if (featureBanner) featureBanner.hidden = selectedId !== '1';

    const shelf = section.querySelector('.le-home-tabbed-products__shelf');
    if (shelf) shelf.scrollLeft = 0;
  }
}

new LetoileHomeTabs();
